import React from 'react';
import styled from 'styled-components';
import Layout from './Layout';
import SEO from './SEO';
import Button from './Button';
import InternalLinks from './InternalLinks';
import { FaCheck, FaQuestionCircle, FaStar, FaTrophy, FaThumbsUp, FaTimes } from 'react-icons/fa';
import TemplateShowcase from './TemplateShowcase';
import ColoredBadge from './ColoredBadge';

// Contenedor principal con estilos modernos
const MainContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

// Hero section con gradiente y diseño moderno
const HeroSection = styled.section`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 3rem 0;
  margin-bottom: 3rem;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  @media (min-width: 992px) {
    flex-direction: row;
    text-align: left;
    align-items: center;
    justify-content: space-between;
  }
`;

const HeroText = styled.div`
  flex: 1;
  
  @media (min-width: 992px) {
    padding-right: 2rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  opacity: 0.9;
  line-height: 1.5;
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  justify-content: center;
  
  @media (min-width: 992px) {
    justify-content: flex-start;
  }
`;

const Badge = styled.span`
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const HeroCTA = styled.div`
  margin-top: 1rem;
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

// Sección de precios con estilo de tarjeta
const PricingSection = styled.section`
  margin: 4rem 0;
`;

const PricingCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  position: relative;
  overflow: hidden;
  text-align: center;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-top: 4px solid #007bff;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const PricingTagPopular = styled.div`
  position: absolute;
  top: 1rem;
  right: -2.5rem;
  background: #28a745;
  color: white;
  padding: 0.25rem 3rem;
  transform: rotate(45deg);
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
`;

const PricingName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const PricingDescription = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const PriceTag = styled.div`
  margin-bottom: 1.5rem;
`;

const CurrentPrice = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: #007bff;
`;

const Period = styled.span`
  font-size: 1rem;
  color: #666;
`;

const Maintenance = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 0.25rem;
`;

const FeaturesList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  
  svg {
    color: #28a745;
    margin-right: 0.5rem;
    min-width: 16px;
    margin-top: 0.25rem;
  }
  
  &.not-included {
    opacity: 0.6;
    
    svg {
      color: #dc3545;
    }
  }
`;

// Proceso paso a paso
const ProcessSection = styled.section`
  margin: 4rem 0;
  padding: 4rem 0;
  background-color: #f8f9fa;
`;

const ProcessTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    width: 100px;
    height: 4px;
    background: #007bff;
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProcessStep = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const StepNumber = styled.div`
  background-color: #007bff;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0.5rem 1.5rem;
  display: inline-block;
  border-radius: 0 0 1rem 0;
  position: absolute;
  top: 0;
  left: 0;
`;

const StepImageContainer = styled.div`
  position: relative;
  padding-top: 60%;
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
`;

const StepIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  color: #007bff;
`;

const StepContent = styled.div`
  padding: 2rem;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
`;

const StepDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

// Comparativa de ventajas
const ComparisonSection = styled.section`
  margin: 4rem 0;
`;

const ComparisonTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    width: 100px;
    height: 4px;
    background: #007bff;
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ComparisonTable = styled.div`
  max-width: 900px;
  margin: 0 auto;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
`;

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 1.5rem;
`;

const ComparisonHeaderItem = styled.div`
  text-align: center;
  padding: 1rem;
  font-size: 1.25rem;
  font-weight: bold;
  
  &:first-child {
    text-align: left;
  }
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid #eee;
  
  &:nth-child(odd) {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ComparisonItem = styled.div`
  padding: 1.25rem;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  
  &:first-child {
    font-weight: 600;
    color: #333;
  }
  
  svg {
    color: #28a745;
    margin-right: 0.75rem;
    font-size: 1.1rem;
  }
  
  &.negative {
    svg {
      color: #dc3545;
    }
  }
`;

// FAQ mejorada
const FAQSection = styled.section`
  margin: 4rem 0;
`;

const FAQTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    width: 100px;
    height: 4px;
    background: #007bff;
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const FAQTabs = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FAQTab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid #ddd;
  border-radius: 2rem;
  padding: 0.5rem 1.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }
`;

const FAQList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  text-align: left;
  padding: 1.25rem;
  background: #f8f9fa;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.3s ease;

  &:hover {
    background: #e9ecef;
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  padding: ${props => props.isOpen ? '1.25rem' : '0 1.25rem'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => props.isOpen ? '1' : '0'};
`;

// Características con iconos de verificación
const FeaturesSection = styled.section`
  margin: 4rem 0;
`;

const FeaturesTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    width: 100px;
    height: 4px;
    background: #007bff;
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  margin-bottom: 1rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 1.5rem;
    color: #007bff;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
  color: #333;
`;

const FeatureDescription = styled.p`
  color: #666;
  margin-bottom: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  flex-grow: 1;
`;

// Testimonios
const TestimonialsSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 4rem 0;
  margin: 4rem 0;
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &:before {
    content: '"';
    position: absolute;
    top: 1rem;
    left: 1.5rem;
    font-size: 4rem;
    color: #f0f0f0;
    line-height: 1;
    font-family: serif;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  padding-left: 1rem;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const TestimonialAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TestimonialInfo = styled.div``;

const TestimonialName = styled.h4`
  margin: 0;
  font-size: 1rem;
  color: #333;
`;

const TestimonialCompany = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #666;
`;

// Sección de beneficios
const BenefitsSection = styled.section`
  margin: 4rem 0;
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const BenefitIconContainer = styled.div`
  margin-right: 1rem;
  
  svg {
    color: #28a745;
    font-size: 1.2rem;
  }
`;

const BenefitContent = styled.div``;

const BenefitTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const BenefitDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
`;

// CTA final
const CTASection = styled.section`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 4rem 0;
  text-align: center;
  border-radius: 0.75rem;
  margin: 4rem 0;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const WhiteButton = styled(Button)`
  background-color: white !important;
  color: #007bff !important;
  border: none !important;
  padding: 0.75rem 2rem !important;
  font-size: 1.1rem !important;
  
  &:hover {
    background-color: #f8f9fa !important;
    color: #0056b3 !important;
    transform: translateY(-2px);
  }
`;

// Tipos de datos
interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FAQ {
  question: string;
  answer: string;
  category?: string;
}

interface LinkItem {
  url: string;
  text: string;
}

interface Benefit {
  title: string;
  description: string;
}

interface Testimonial {
  text: string;
  author: string;
  position: string;
  company: string;
  avatar?: string;
}

// Tipos para el pricing
interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  title: string;
  price: number;
  period?: string;
  description: string;
  features: PlanFeature[];
  isPopular?: boolean;
  buttonText: string;
  buttonLink: string;
  maintenanceFee?: string;
}

// Tipo para el proceso
interface ProcessStepItem {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// Tipos para la comparativa
interface ComparisonItem {
  label: string;
  us: boolean;
  others: boolean;
}

interface HostingerStyleServiceTemplateProps {
  // SEO
  pageTitle: string;
  pageDescription: string;
  pageKeywords: string;
  canonicalUrl: string;
  
  // Hero
  heroTitle: string;
  heroSubtitle: string;
  heroImage?: string;
  badges?: string[];
  heroCTAText?: string;
  heroCTALink?: string;
  
  // Pricing
  pricingTitle?: string;
  pricingSubtitle?: string;
  plans: Plan[];
  
  // Features
  featuresTitle?: string;
  features: Feature[];
  
  // Custom Content
  customContent?: React.ReactNode;
  
  // Process
  processTitle?: string;
  processSteps?: ProcessStepItem[];
  
  // Comparison
  comparisonTitle?: string;
  comparisonItems?: ComparisonItem[];
  
  // Testimonial
  testimonial?: Testimonial;
  
  // Benefits
  benefitsTitle?: string;
  benefits: Benefit[];
  
  // FAQs
  faqTitle?: string;
  faqCategories?: string[];
  faqs: FAQ[];
  
  // Final CTA
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonText?: string;
  ctaButtonLink?: string;
  
  // Related links
  relatedLinks: LinkItem[];
}

const HostingerStyleServiceTemplate: React.FC<HostingerStyleServiceTemplateProps> = ({
  // SEO
  pageTitle,
  pageDescription,
  pageKeywords,
  canonicalUrl,
  
  // Hero
  heroTitle,
  heroSubtitle,
  heroImage,
  badges = [],
  heroCTAText = "Solicitar presupuesto",
  heroCTALink = "/contacto",
  
  // Pricing
  pricingTitle = "Planes a tu medida",
  pricingSubtitle = "Elige el plan que mejor se adapte a tus necesidades",
  plans,
  
  // Features
  featuresTitle = "Características incluidas",
  features,
  
  // Custom Content
  customContent,
  
  // Process
  processTitle = "Nuestro proceso de trabajo",
  processSteps = [],
  
  // Comparison
  comparisonTitle = "¿Por qué elegirnos?",
  comparisonItems = [],
  
  // Testimonial
  testimonial,
  
  // Benefits
  benefitsTitle = "Beneficios",
  benefits,
  
  // FAQs
  faqTitle = "Preguntas frecuentes",
  faqCategories = [],
  faqs,
  
  // Final CTA
  ctaTitle = "¿Listo para empezar?",
  ctaDescription = "Impulsa tu presencia online con una web profesional adaptada a tus necesidades",
  ctaButtonText = "Contactar ahora",
  ctaButtonLink = "/contacto",
  
  // Related links
  relatedLinks,
}) => {
  const [activeCategory, setActiveCategory] = React.useState<string>(faqCategories.length > 0 ? faqCategories[0] : "");
  const [openFaqIds, setOpenFaqIds] = React.useState<number[]>([]);
  
  const filteredFaqs = faqCategories.length > 0 
    ? faqs.filter(faq => faq.category === activeCategory)
    : faqs;
    
  const toggleFaq = (index: number) => {
    if (openFaqIds.includes(index)) {
      setOpenFaqIds(openFaqIds.filter(id => id !== index));
    } else {
      setOpenFaqIds([...openFaqIds, index]);
    }
  };

  return (
    <Layout>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical={canonicalUrl}
      />
      
      <HeroSection>
        <MainContainer>
          <HeroContent>
            <HeroText>
              <HeroTitle>{heroTitle}</HeroTitle>
              <HeroSubtitle>{heroSubtitle}</HeroSubtitle>
              
              <HeroBadges>
                {badges.map((badge, index) => (
                  <ColoredBadge key={index} index={index}>
                    {badge}
                  </ColoredBadge>
                ))}
              </HeroBadges>
              
              <HeroCTA>
                <Button 
                  as="a" 
                  href={heroCTALink} 
                  size="large"
                >
                  {heroCTAText}
                </Button>
              </HeroCTA>
            </HeroText>
            
            {heroImage && (
              <HeroImage>
                <img src={heroImage} alt={heroTitle} />
              </HeroImage>
            )}
          </HeroContent>
        </MainContainer>
      </HeroSection>
      
      <MainContainer>
        <PricingSection>
          {pricingTitle && (
            <FeaturesTitle>{pricingTitle}</FeaturesTitle>
          )}
          {pricingSubtitle && (
            <div style={{ textAlign: 'center', maxWidth: '700px', margin: '-2rem auto 2rem', color: '#666' }}>
              <p>{pricingSubtitle}</p>
            </div>
          )}
          
          <PricingCardsGrid>
            {plans.map((plan, index) => (
              <PricingCard key={index}>
                {plan.isPopular && <PricingTagPopular>POPULAR</PricingTagPopular>}
                <PricingName>{plan.title}</PricingName>
                <PricingDescription>{plan.description}</PricingDescription>
                
                <PriceTag>
                  <CurrentPrice>{plan.price}€</CurrentPrice> <Period>{plan.period || "pago único"}</Period>
                </PriceTag>
                
                <Maintenance>
                  <strong>Mantenimiento:</strong> {plan.maintenanceFee || "20€/mes"}
                </Maintenance>
                
                <FeaturesList>
                  {plan.features.map((feature, featureIndex) => (
                    <FeatureItem 
                      key={featureIndex}
                      className={!feature.included ? "not-included" : ""}
                    >
                      {feature.included ? <FaCheck size={16} /> : <FaTimes size={16} />} {feature.text}
                    </FeatureItem>
                  ))}
                </FeaturesList>
                
                <div style={{ marginTop: 'auto' }}>
                  <Button 
                    as="a" 
                    href={plan.buttonLink} 
                    size="large" 
                    fullWidth
                  >
                    {plan.buttonText}
                  </Button>
                </div>
              </PricingCard>
            ))}
          </PricingCardsGrid>
        </PricingSection>
        
        <BenefitsSection>
          <FeaturesTitle>{benefitsTitle}</FeaturesTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIconContainer>
                  <FaCheck />
                </BenefitIconContainer>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </BenefitsSection>
        
        <TemplateShowcase />
        
        <FeaturesSection>
          <FeaturesTitle>{featuresTitle}</FeaturesTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>
                  {feature.icon}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>
                  {feature.description}
                </FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>
        
        {/* Custom Content Section */}
        {customContent && customContent}
        
        {processSteps.length > 0 && (
          <ProcessSection>
            <MainContainer>
              <ProcessTitle>{processTitle}</ProcessTitle>
              <ProcessSteps>
                {processSteps.slice(0, 3).map((step) => (
                  <ProcessStep key={step.number}>
                    <StepImageContainer>
                      <StepNumber>{step.number}</StepNumber>
                      <StepIcon>{step.icon}</StepIcon>
                    </StepImageContainer>
                    <StepContent>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </StepContent>
                  </ProcessStep>
                ))}
              </ProcessSteps>
            </MainContainer>
          </ProcessSection>
        )}
        
        {comparisonItems.length > 0 && (
          <ComparisonSection>
            <FeaturesTitle>{comparisonTitle}</FeaturesTitle>
            <ComparisonTable>
              <ComparisonHeader>
                <ComparisonHeaderItem>Características</ComparisonHeaderItem>
                <ComparisonHeaderItem>Web a Bajo Coste</ComparisonHeaderItem>
                <ComparisonHeaderItem>Otras agencias</ComparisonHeaderItem>
              </ComparisonHeader>
              
              {comparisonItems.map((item, index) => (
                <ComparisonRow key={index}>
                  <ComparisonItem>
                    {item.label}
                  </ComparisonItem>
                  <ComparisonItem className={item.us ? "" : "negative"}>
                    {item.us ? <FaCheck /> : <FaTimes />} {item.us ? "Sí" : "No"}
                  </ComparisonItem>
                  <ComparisonItem className={item.others ? "" : "negative"}>
                    {item.others ? <FaCheck /> : <FaTimes />} {item.others ? "Sí" : "No"}
                  </ComparisonItem>
                </ComparisonRow>
              ))}
            </ComparisonTable>
          </ComparisonSection>
        )}
        
        {testimonial && (
          <TestimonialsSection>
            <MainContainer>
              <TestimonialCard>
                <TestimonialText>
                  {testimonial.text}
                </TestimonialText>
                <TestimonialAuthor>
                  {testimonial.avatar && (
                    <TestimonialAvatar>
                      <img src={testimonial.avatar} alt={testimonial.author} />
                    </TestimonialAvatar>
                  )}
                  <TestimonialInfo>
                    <TestimonialName>{testimonial.author}</TestimonialName>
                    <TestimonialCompany>
                      {testimonial.position}, {testimonial.company}
                    </TestimonialCompany>
                  </TestimonialInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </MainContainer>
          </TestimonialsSection>
        )}
        
        <FAQSection>
          <FAQTitle>{faqTitle}</FAQTitle>
          
          {faqCategories.length > 0 && (
            <FAQTabs>
              {faqCategories.map((category) => (
                <FAQTab 
                  key={category}
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </FAQTab>
              ))}
            </FAQTabs>
          )}
          
          <FAQList>
            {filteredFaqs.map((faq, index) => {
              const isOpen = openFaqIds.includes(index);
              return (
                <FAQItem key={index}>
                  <FAQQuestion onClick={() => toggleFaq(index)}>
                    {faq.question}
                    {isOpen ? (
                      <FaTimes size={16} />
                    ) : (
                      <FaQuestionCircle size={16} />
                    )}
                  </FAQQuestion>
                  <FAQAnswer isOpen={isOpen}>
                    {faq.answer}
                  </FAQAnswer>
                </FAQItem>
              );
            })}
          </FAQList>
        </FAQSection>
        
        <CTASection>
          <MainContainer>
            <CTATitle>{ctaTitle}</CTATitle>
            <CTADescription>{ctaDescription}</CTADescription>
            <WhiteButton 
              as="a" 
              href={ctaButtonLink}
              size="large"
            >
              {ctaButtonText}
            </WhiteButton>
          </MainContainer>
        </CTASection>
        
        <InternalLinks 
          title="Servicios relacionados que podrían interesarte"
          links={relatedLinks}
        />
      </MainContainer>
    </Layout>
  );
};

export default HostingerStyleServiceTemplate; 